// Shared formatting utilities for numbers, currencies, quantities and dates.
// Use Intl APIs when available, with sensible fallbacks.

// Small helper to ensure there is a non-breaking space between the currency symbol and the number
export function ensureCurrencySpace(str) {
    if (typeof str !== 'string') return str;

    let s = str.replace(/\u202F/g, '\u00A0');

    // If symbol is before the number (e.g. "$72.50" or "€72,50"), insert NBSP between them
    s = s.replace(/^([^\d\s]+)\s*([+\-]?\(?\d)/, '$1\u00A0$2');

    // If number is before the symbol (e.g. "72,50 €"), ensure NBSP between them (handle leading sign/parentheses)
    s = s.replace(/([+\-]?\(?[\d\.,\u00A0]+)\s*([^\d\s]+)$/, '$1\u00A0$2');

    return s;
}

// Calculate a price (unitPrice * quantity) and return a formatted string using locale and currency.
export function formatCalculatedPrice(unitPrice, quantity, locale = 'en-US', currency = 'USD', opts = {}) {
    const u = parseLocaleNumber(unitPrice, locale);
    const q = parseLocaleNumber(quantity, locale);

    const unit = u === null ? 0 : u;
    const qty = q === null ? 0 : q;

    const total = unit * qty;

    return formatCurrency(total, locale, currency, opts);
}

export function formatCurrency(value, locale = 'en-US', currency = 'USD', opts = {}) {
    console.log('value is: ',value)
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const num = parseLocaleNumber(value, locale);
    if (num === null) {
        return String(value);
    }

    const options = Object.assign(
        {
            currency,
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: 'currency',
        },
        opts
    );

    if (typeof Intl === 'object' && Intl && typeof Intl.NumberFormat === 'function') {
        const formatter = new Intl.NumberFormat(locale, options);
        const formatted = formatter.format(num);
        return ensureCurrencySpace(formatted);
    }

    const fallbackFormatted = num.toLocaleString(locale, options);
    return ensureCurrencySpace(fallbackFormatted);
}

// Format a Date (or parsable date string / timestamp) using Intl.DateTimeFormat when available.
// opts is passed directly to Intl.DateTimeFormat options (eg. { dateStyle: 'short' }).
export function formatDate(value, locale = 'en-US', opts = { dateStyle: 'short' }) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    let d;
    if (value instanceof Date) {
        d = value;
    } else {
        d = new Date(value);
    }

    if (isNaN(d.getTime())) {
        return String(value);
    }

    const options = Object.assign({}, opts);

    // If no explicit timeZone is provided, prefer the client's local timezone
    // (resolved via Intl) to make formatting consistent with the user's environment.
    if (!options.timeZone && typeof Intl === 'object' && Intl && typeof Intl.DateTimeFormat === 'function') {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
            options.timeZone = tz;
        }
    }

    if (typeof Intl === 'object' && Intl && typeof Intl.DateTimeFormat === 'function') {
        const formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(d);
    }

    try {
        return d.toLocaleDateString(locale, options);
    } catch {
        return d.toISOString();
    }
}

export function formatNumber(value, locale = 'en-US', opts = {}) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const num = parseLocaleNumber(value, locale);
    if (num === null) {
        return String(value);
    }

    const options = Object.assign(
        {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: 'decimal',
        },
        opts
    );

    if (typeof Intl === 'object' && Intl && typeof Intl.NumberFormat === 'function') {
        const formatter = new Intl.NumberFormat(locale, options);
        return formatter.format(num);
    }

    return num.toLocaleString(locale, options);
}

export function formatNumberForTwoDecimal(value, locale = 'en-US', opts = {}) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const num = parseLocaleNumber(value, locale);
    if (num === null) {
        return String(value);
    }

    const options = Object.assign(
        {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: 'decimal',
        },
        opts
    );

    if (typeof Intl === 'object' && Intl && typeof Intl.NumberFormat === 'function') {
        const formatter = new Intl.NumberFormat(locale, options);
        return formatter.format(num);
    }

    return num.toLocaleString(locale, options);
}

// Format quantity: backend rounds to nearest long. Mirror that: round to nearest integer and return string.
export function formatQuantity(value, locale = 'en-US') {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const num = parseLocaleNumber(value, locale);
    if (num === null) {
        return String(value);
    }

    const rounded = Math.round(num);
    const options = {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: 'decimal',
        useGrouping: true,
    };

    if (typeof Intl === 'object' && Intl && typeof Intl.NumberFormat === 'function') {
        const formatter = new Intl.NumberFormat(locale, options);
        return formatter.format(rounded);
    }

    return rounded.toLocaleString(locale, options);
}

// Expects date in format DD.MM.YYYY or DD//MM/YYYY
export function formatSAPDate(
    dateStr,
    opts = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }
) {
    if (!dateStr || dateStr === '00.00.0000' || dateStr === '00/00/0000') {
        return '';
    }
    const [day, month, year] = dateStr.split(/[./]/).map(Number);
    const dateObj = new Date(year, month - 1, day);

    if (isNaN(dateObj.getTime())) {
        return 'Invalid Date';
    }

    return dateObj.toLocaleDateString(window.Liferay.ThemeDisplay.getBCP47LanguageId(), opts);
}

//Expects date in format YYYYMMDD
export function formatSAPDateForReports(
    dateStr,
    opts = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }
) {
    const date = new Date(Date.UTC(dateStr.slice(0, 4), Number(dateStr.slice(4, 6)) - 1, dateStr.slice(6, 8)));
    return date.toLocaleDateString(window.Liferay.ThemeDisplay.getBCP47LanguageId(), opts);
}

// SEMI SAP sends plain decimal strings (e.g. "8381.760") and a currency symbol instead of an ISO code.
// Intl only formats currencies by ISO code, so format with a placeholder currency and swap in the symbol;
// the symbol position and spacing then follow the locale ("$8,381.76" in en-US, "8.381,76 $" in de-DE).
export function formatSAPDecimalPrice(amountStr, currencySymbol) {
    if (!currencySymbol) {
        return formatSAPDecimalValue(amountStr);
    }

    return new Intl.NumberFormat(window.Liferay.ThemeDisplay.getBCP47LanguageId(), {
        currency: 'USD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
    })
        .formatToParts(amountStr ?? 0)
        .map(part => (part.type === 'currency' ? currencySymbol : part.value))
        .join('');
}

// SEMI SAP sends plain decimal strings (e.g. "8381.760"), which Intl.NumberFormat formats as they are
export function formatSAPDecimalQuantity(amountStr) {
    return new Intl.NumberFormat(window.Liferay.ThemeDisplay.getBCP47LanguageId(), {
        maximumFractionDigits: 3,
        style: 'decimal',
    }).format(amountStr ?? 0);
}

// SEMI SAP sends plain decimal strings (e.g. "8381.760"), which Intl.NumberFormat formats as they are
export function formatSAPDecimalValue(amountStr, currencyCode) {
    const options = currencyCode
        ? { currency: currencyCode, style: 'currency' }
        : { maximumFractionDigits: 2, minimumFractionDigits: 2, style: 'decimal' };

    return new Intl.NumberFormat(window.Liferay.ThemeDisplay.getBCP47LanguageId(), options).format(amountStr ?? 0);
}

// SAP always sends numbers in XXX.XXX.XXX,00 format, regardless of the user locale
export function parseSAPNumber(amountStr) {
    if (!amountStr) {
        return 0;
    }
    return parseFloat(String(amountStr).replace(/\./g, '').replace(',', '.'));
}

//Expects value in XXX.XXX.XXX,000,format
export function formatSAPQuantity(amountStr) {
    const numericAmount = parseSAPNumber(amountStr);

    return new Intl.NumberFormat(window.Liferay.ThemeDisplay.getBCP47LanguageId(), {
        maximumFractionDigits: 0,
        style: 'decimal',
    }).format(numericAmount);
}

//Expects value in XXX.XXX.XXX,00, format
export function formatSAPValue(amountStr, currencyCode) {
    if (!amountStr) {
        amountStr = '0,00';
    }
    if (!currencyCode) {
        return formatSAPQuantity(amountStr);
    }

    const numericAmount = parseSAPNumber(amountStr);

    return new Intl.NumberFormat(window.Liferay.ThemeDisplay.getBCP47LanguageId(), {
        currency: currencyCode,
        style: 'currency',
    }).format(numericAmount);
}

export function formatDateForAPI(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
}

// Convenience dispatcher to format values based on a type. Supported: 'currency','number','quantity','date'
export function localize(value, type = 'number', locale = 'en-US', opts = {}) {
    switch (type) {
        case 'currency':
            // expects opts.currency to be set, fallback to USD
            return formatCurrency(value, locale, opts.currency || 'USD', opts);
        case 'date':
            return formatDate(value, locale, opts);
        case 'number':
            return formatNumber(value, locale, opts);
        case 'quantity':
            return formatQuantity(value);
        default:
            return String(value);
    }
}

export function parseLocaleNumber(value, locale) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (typeof value === 'number') {
        return value;
    }

    let s = String(value).trim();
    s = s.replace(/\u00A0/g, '');
    s = s.replace(/[^0-9,.-]/g, '');

    const separatorsByLocale = {
        'cs-CZ': { decimal: ',', group: '\u202F' },
        'de-DE': { decimal: ',', group: '.' },
        'en-CA': { decimal: '.', group: ',' },
        'en-GB': { decimal: '.', group: ',' },
        'en-IN': { decimal: '.', group: ',' },
        'en-US': { decimal: '.', group: ',' },
        'es-ES': { decimal: ',', group: '.' },
        'es-MX': { decimal: '.', group: ',' },
        'fi-FI': { decimal: ',', group: '\u202F' },
        'fr-CA': { decimal: ',', group: ' ' },
        'fr-FR': { decimal: ',', group: '\u202F' }, // narrow no-break space
        'hu-HU': { decimal: ',', group: '\u202F' },
        'it-IT': { decimal: ',', group: '.' },
        'ja-JP': { decimal: '.', group: ',' },
        'ko-KR': { decimal: '.', group: ',' },
        'pl-PL': { decimal: ',', group: '\u202F' },
        'pt-BR': { decimal: ',', group: '.' },
        'ru-RU': { decimal: ',', group: ' ' },
        'th-TH': { decimal: '.', group: ',' },
        'tr-TR': { decimal: ',', group: '.' },
        'zh-CN': { decimal: '.', group: ',' },
        'zh-TW': { decimal: '.', group: ',' },
    };

    if (locale) {
        const key = locale.replace('_', '-');
        const sep = separatorsByLocale[key] || separatorsByLocale[key.split('-')[0]];
        if (sep) {
            const group = sep.group;
            const dec = sep.decimal;
            const groupRegex = new RegExp(group.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
            s = s.replace(groupRegex, '');
            if (dec !== '.') {
                s = s.replace(dec, '.');
            }
            const nLocale = Number(s);
            return Number.isNaN(nLocale) ? null : nLocale;
        }
    }

    if (!/[0-9]/.test(s)) {
        return null;
    }

    // If both dot and comma exist, decide which is decimal separator
    const hasComma = s.indexOf(',') !== -1;
    const hasDot = s.indexOf('.') !== -1;

    if (hasComma && hasDot) {
        if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
            s = s.replace(/\./g, '').replace(',', '.');
        } else {
            s = s.replace(/,/g, '');
        }

        const n = Number(s);
        return Number.isNaN(n) ? null : n;
    }

    if (hasComma && !hasDot) {
        s = s.replace(',', '.');
        const n = Number(s);
        return Number.isNaN(n) ? null : n;
    }

    const n = Number(s);
    return Number.isNaN(n) ? null : n;
}

export default {
    ensureCurrencySpace,
    formatCalculatedPrice,
    formatCurrency,
    formatDate,
    formatNumber,
    formatNumberForTwoDecimal,
    formatQuantity,
    formatSAPDate,
    formatSAPDateForReports,
    formatSAPDecimalPrice,
    formatSAPDecimalQuantity,
    formatSAPDecimalValue,
    formatSAPQuantity,
    formatSAPValue,
    formatDateForAPI,
    localize,
    parseLocaleNumber,
    parseSAPNumber,
};

// Expose for legacy/global usage under `window.ams.formatUtils`.
// This makes the helpers available to non-module code or inline scripts
// without changing module consumers.
if (typeof window !== 'undefined') {
    window.ams = window.ams || {};
    window.ams.formatUtils = window.ams.formatUtils || {
        ensureCurrencySpace,
        formatCalculatedPrice,
        formatCurrency,
        formatDate,
        formatNumber,
        formatNumberForTwoDecimal,
        formatQuantity,
        formatSAPDate,
        formatSAPDateForReports,
        formatSAPDecimalPrice,
        formatSAPDecimalQuantity,
        formatSAPDecimalValue,
        formatSAPQuantity,
        formatSAPValue,
        formatDateForAPI,
        localize,
        parseLocaleNumber,
        parseSAPNumber,
    };
}
